import { action, makeObservable, observable, runInAction } from 'mobx';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Company,
  CompanyBenefit,
  CompanyBenefitService,
  CompanyEmployeeService,
  CompanyEmployeeExternalIdService,
  CompanyService,
  CompanyEmployee } from 'us-web-services';
import { UserStore } from '../UserStore';
import { Storage } from '../../constants';
import { companyBenefitStatusIds } from '../../constants/CompanyBenefit';

export class CompanyEmployeeStore {
  #userId?: number;
  #saving: boolean;
  #companyEmployeeId?: number;
  companies: Company[] = [];
  companyBenefits: CompanyBenefit[] = [];
  companyEmployees: CompanyEmployee[] = [];

  constructor(user: UserStore) {
    this.#userId = user.user.id;
    this.#saving = false;

    makeObservable(this, {
      companies: observable,
      addBenefits: action,
    });

    if (this.#userId) {
      this.fetchCompanyEmployees();
    }
  }

  async fetchCompanyEmployees(): Promise<void> {
    this.companies = [];
    this.companyBenefits = [];
    this.companyEmployees = [];

    const config = {
      params: {
        userId: this.#userId,
      }
    }

    const response = (await CompanyEmployeeService.findByFilter(config)).data;
    this.companyEmployees = response?.data || [];

    await Promise.allSettled(this.companyEmployees.map(async companyEmployee => {
      if (companyEmployee.companyId && !companyEmployee.disabled) {
        const companyResponse = (await CompanyService.get(companyEmployee.companyId)).data;
        this.companies.push(companyResponse.data);

        const benefitConfig = {
          params: {
            companyId: companyEmployee.companyId,
            statusId: companyBenefitStatusIds.ACTIVE,
          }
        };

        const benefitsResponse = (await CompanyBenefitService.getByFilter(benefitConfig)).data;
        benefitsResponse.data.forEach(benefit => {
          const startDate = new Date(benefit.start || '');
          const endDate = new Date(benefit.end || '');
          const today = new Date();
          if ((!benefit.start || startDate < today) && endDate > today) {
            this.companyBenefits.push(benefit);
          }
        });
      }
    }));
  }

  static removeLocalStorage(): void {
    localStorage.removeItem(Storage.COMPANY_EXTERNAL_ID);
    localStorage.removeItem(Storage.COMPANY_ID);
  }

  addBenefits = async (): Promise<void> => {
    const companyId = localStorage.getItem(Storage.COMPANY_ID);

    const config = {
      params: {
        companyId,
        userId: this.#userId,
      },
    };

    const response = (await CompanyEmployeeService.findByFilter(config)).data;

    runInAction(() => {
      if (response.meta.size > 0) {
        const firstEmployee = response.data.shift();
        this.#companyEmployeeId = firstEmployee?.id;
        CompanyEmployeeStore.removeLocalStorage();
      } else {
        this.validateUniqueExternalId();
      }
    });
  }

  validateUniqueExternalId = async (): Promise<void> => {
    const { t } = useTranslation();
    const companyId = localStorage.getItem(Storage.COMPANY_ID);
    const externalId = localStorage.getItem(Storage.COMPANY_EXTERNAL_ID);

    const config = {
      params: {
        companyId,
        externalId,
      }
    }
    const response = (await CompanyEmployeeExternalIdService.getByFilter(config)).data;
    runInAction(() => {
      if (response.meta.size > 0) {
          toast.error(t('dashboard.benefits.notRedeemed'));
          CompanyEmployeeStore.removeLocalStorage();
      } else {
          this.saveBenefits();
      }
    });
  }

  saveBenefits = async (): Promise<void> => {
    const { t } = useTranslation();
    const externalId = localStorage.getItem(Storage.COMPANY_EXTERNAL_ID) ?? 0;
    const companyId = localStorage.getItem(Storage.COMPANY_ID) ?? 0;

    if (externalId && companyId && this.#companyEmployeeId && !this.#saving) {
      const data = {
        externalId,
        companyId: parseInt(companyId, 10),
        userId: this.#userId,
      };

      this.#saving = true;
      const response = (await CompanyEmployeeService.patch(this.#companyEmployeeId, data)).data;

      if (response.data?.companyId) {
        toast.success(t('dashboard.benefits.benefitsActivated'));
        CompanyEmployeeStore.removeLocalStorage();
      }

      this.#saving = false;
    }
  }
}
